import type { ReactNode } from 'react';

import _ from 'lodash';
import {
  BadgeDollarSignIcon,
  CircleCheckIcon,
  CoinsIcon,
  RouteIcon,
  Truck,
} from 'lucide-react';

import type { QuoteCardType } from 'components/QuoteCard';
import { TQLTruPayLogo, TQL_BRAND_BLUE } from 'icons/TQLTruPayLogo';
import { titleCase } from 'utils/formatStrings';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';
import { Flex } from './layout';
import { Typography } from './typography';

type QuoteCardTruPayProps = {
  carrier: QuoteCardType;
  isSelected: boolean;
  onClick: () => void;
};

const usd = (amount: number) => `$${_.round(amount).toLocaleString()}`;
const usdPerMile = (amount: number) => `$${_.round(amount, 2).toFixed(2)}/mi`;
const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

function Row({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <Flex justify='between' align='center' className='w-full gap-2'>
      <Flex align='center' gap='sm' className='shrink-0'>
        {icon}
        <Typography variant='body-sm'>{label}</Typography>
      </Flex>
      <Flex
        align='baseline'
        className='min-w-0 gap-x-2 flex-wrap justify-end text-right'
      >
        {children}
      </Flex>
    </Flex>
  );
}

function DashedTooltip({
  trigger,
  children,
}: {
  trigger: ReactNode;
  children: ReactNode;
}) {
  return (
    <Tooltip delayDuration={10}>
      <TooltipTrigger className='border-b border-dashed border-neutral-900'>
        {trigger}
      </TooltipTrigger>
      <TooltipPortal container={getDrumkitContainer()}>
        <TooltipContent className='mr-2 max-w-64 whitespace-pre-wrap'>
          {children}
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
}

export function QuoteCardTruPay({
  carrier,
  isSelected,
  onClick,
}: QuoteCardTruPayProps) {
  const meta = carrier.trupayMetadata;
  if (!meta) {
    return null;
  }

  const isProxiedEquipment =
    carrier.inputtedTransportType !== carrier.actualTransportType;
  const range = carrier.priceRange;
  const rangePerMile = carrier.priceRangePerMile;
  const isMultiStop = meta.pickupCount + meta.dropCount > 2;

  return (
    <TooltipProvider>
      <Flex
        direction='col'
        className={cn(
          'relative w-full min-w-0 rounded-[4px] border-2 p-2 transition-transform',
          isSelected ? 'bg-white' : 'cursor-pointer hover:scale-[1.01] bg-white'
        )}
        style={{
          borderColor: TQL_BRAND_BLUE,
          ...(isSelected ? { backgroundColor: `${TQL_BRAND_BLUE}0d` } : {}),
        }}
        onClick={onClick}
        data-testid='quote-card-trupay'
      >
        <Flex align='center' className='w-full mb-2'>
          <TQLTruPayLogo className='min-w-0' />
        </Flex>

        <Flex direction='col' className='w-full gap-1'>
          <Row icon={<Truck className='h-4 w-4' />} label='Transport Type'>
            {isProxiedEquipment ? (
              <DashedTooltip
                trigger={
                  <Typography variant='body-sm'>
                    {titleCase(carrier.actualTransportType)}
                  </Typography>
                }
              >
                <Typography variant='body-xs'>
                  TruPay prices Van, Reefer, and Flatbed. Showing the{' '}
                  {titleCase(carrier.actualTransportType)} rate for{' '}
                  {titleCase(carrier.inputtedTransportType)}.
                </Typography>
              </DashedTooltip>
            ) : (
              <Typography variant='body-sm'>
                {titleCase(carrier.actualTransportType)}
              </Typography>
            )}
          </Row>

          <Row
            icon={<BadgeDollarSignIcon className='h-4 w-4' />}
            label='Buy Rate'
          >
            <Typography
              variant='body-xs'
              weight='semibold'
              className='tabular-nums'
              data-testid='trupay-target-rate'
            >
              {usd(carrier.cost)}
            </Typography>
            {carrier.costPerMile != null && (
              <Typography variant='body-xs' className='tabular-nums'>
                ({usdPerMile(carrier.costPerMile)})
              </Typography>
            )}
          </Row>

          {range && (
            <Row icon={<CoinsIcon className='h-4 w-4' />} label='Price Range'>
              <DashedTooltip
                trigger={
                  <Typography
                    variant='body-xs'
                    className='tabular-nums'
                    data-testid='trupay-price-range'
                  >
                    {usd(range.lowEstimate)} – {usd(range.highEstimate)}
                  </Typography>
                }
              >
                {rangePerMile &&
                  rangePerMile.lowEstimate > 0 &&
                  rangePerMile.highEstimate > 0 && (
                    <div className='pb-3'>
                      <Typography variant='body-xs'>
                        Price range per mile
                      </Typography>
                      <Typography variant='body-xs'>
                        {usdPerMile(rangePerMile.lowEstimate)} –{' '}
                        {usdPerMile(rangePerMile.highEstimate)}
                      </Typography>
                    </div>
                  )}
                <Typography variant='body-xs'>
                  TruPay's typical band around the prediction. It is not a
                  confidence interval.
                </Typography>
                <Typography variant='body-sm' className='border-t mt-3 pt-3'>
                  All-in linehaul, fuel included.
                </Typography>
              </DashedTooltip>
            </Row>
          )}

          {(meta.distanceMiles != null || isMultiStop) && (
            <Row icon={<RouteIcon className='h-4 w-4' />} label='Route'>
              {isMultiStop && (
                <DashedTooltip
                  trigger={
                    <Typography
                      variant='body-xs'
                      data-testid='trupay-stop-counts'
                    >
                      {plural(meta.pickupCount, 'pickup')} ·{' '}
                      {plural(meta.dropCount, 'drop')}
                    </Typography>
                  }
                >
                  <Typography variant='body-xs'>
                    Priced as one route from the first pickup to the last drop.
                    TruPay adjusts the rate for the extra stops.
                  </Typography>
                </DashedTooltip>
              )}
              {meta.distanceMiles != null && (
                <Typography variant='body-xs' className='tabular-nums'>
                  {Math.round(meta.distanceMiles).toLocaleString()} mi
                </Typography>
              )}
            </Row>
          )}
        </Flex>

        {meta.quoteId && (
          <Typography
            variant='body-xs'
            className='mt-3 block w-full border-t border-neutral-200 pt-2 text-neutral-400 truncate'
            title={meta.quoteId}
          >
            TruPay quote {meta.quoteId}
          </Typography>
        )}

        {isSelected && (
          <CircleCheckIcon
            data-testid='quote-card-selected-check'
            className='absolute -top-3 -right-2.5 h-6 w-6 fill-white stroke-success-500!'
            aria-hidden
          />
        )}
      </Flex>
    </TooltipProvider>
  );
}
